import { IObservableArray, makeAutoObservable, observable, ObservableMap } from 'mobx';
import { Attachment, ChatMessageID, ChatMessageOwner } from '../models/chat';
import { groupChatMessages, sortChatMessages } from '../utils/chatUtils';

export interface IChatConversationMessagePlace {
    id: number;
    entry_id?: number;
    entry_type?: string;
    name?: string;
}

export interface IQuotedChatMessage {
    id: number;
    type: 'message';
    meeting_id: number;
    content: string;
    created_at: string | Date;
    edited_at?: string | Date | null;
    owner: ChatMessageOwner;
}

// This is a representation that should reflect the needs of the UI, not the server.
// It's almost like the message returned from the server BUT not exactly.
export interface IChatConversationMessage {
    id: ChatMessageID;
    guid?: string;
    type: 'message';
    meeting_id: number;
    content: string;

    owner: ChatMessageOwner;
    // Place property is only used by Public Chat to display source of the message
    place?: IChatConversationMessagePlace;
    quoted_message: IQuotedChatMessage | null;
    attachments?: Attachment[];

    created_at: string | Date;
    edited_at?: string | Date | null;
}

// Must be exactly the same as normal message, but without ID - this is important because
// the optimistic message can be displayed in the same places where regular message.
interface IChatOptimisticConversationMessage extends Omit<IChatConversationMessage, 'id'> {
    // ID is not known without getting response from the server
    id: ChatMessageID;
}

/**
 * Represents the state of the chat conversation as seen in the UI.
 * This has some implications:
 *
 * - adding message to this store might be ignored
 */
export class ChatConversationStore {
    readonly messages: IObservableArray<IChatOptimisticConversationMessage | IChatConversationMessage> = observable([]);
    isLoading: boolean = false;
    hasNextMessages: boolean = false;
    hasPreviousMessages: boolean = false;
    searchTerm?: string;
    state: string = '';

    constructor() {
        makeAutoObservable(this);
    }

    get sortedMessages() {
        return this.messages.slice().sort(sortChatMessages);
    }

    get groupedMessages() {
        return groupChatMessages(this.sortedMessages);
    }

    setIsLoading(state: boolean) {
        this.isLoading = state;
    }

    addMessage(newMessage: IChatOptimisticConversationMessage | IChatConversationMessage): void {
        if (this.hasNextMessages) {
            return; // we still can fetch messages from the backend, don't append new messages
        }

        // remove message to be safe that we dont get any duplicates
        this.removeMessage(newMessage);

        this.messages.push(newMessage);
    }

    updateMessage(existingMessage: IChatOptimisticConversationMessage | IChatConversationMessage): void {
        // it's basically the same as adding a message
        this.addMessage(existingMessage);
    }

    removeMessage(message: IChatOptimisticConversationMessage | IChatConversationMessage): void {
        const found = this.messages.find((lookupMessage) => {
            return message.id === lookupMessage.id || message.guid === lookupMessage.guid;
        });

        found && this.messages.remove(found);
    }
}

class ChatStore {
    conversations = new ObservableMap<number, ChatConversationStore>();

    constructor() {
        makeAutoObservable(this);
    }

    public getOrCreateConversation(meetingId: number): ChatConversationStore {
        return (
            this.conversations.get(meetingId) ??
            this.conversations.set(meetingId, new ChatConversationStore()).get(meetingId)!
        );
    }

    setConversationMessages(
        meetingId: number,
        messages: IChatConversationMessage[],
        payload: { hasPreviousMessages?: boolean; hasNextMessages?: boolean }
    ) {
        const conversation = this.getOrCreateConversation(meetingId);
        conversation.messages.replace(messages);
        Object.assign(conversation, payload);
    }

    mergeConversationMessages(
        meetingId: number,
        messages: IChatConversationMessage[],
        payload: { hasPreviousMessages?: boolean; hasNextMessages?: boolean }
    ) {
        const conversation = this.getOrCreateConversation(meetingId);
        const existingMessages = new Set(conversation.messages.map((m) => m.id));

        messages.forEach((newMessage) => {
            !existingMessages.has(newMessage.id) && conversation.messages.push(newMessage);
        });

        Object.assign(conversation, payload);
    }

    addConversationMessage(meetingId: number, message: IChatOptimisticConversationMessage | IChatConversationMessage) {
        this.getOrCreateConversation(meetingId).addMessage(message);
    }

    updateConversationMessage(
        meetingId: number,
        message: IChatOptimisticConversationMessage | IChatConversationMessage
    ) {
        this.getOrCreateConversation(meetingId).updateMessage(message);
    }

    removeConversationMessage(
        meetingId: number,
        message: IChatOptimisticConversationMessage | IChatConversationMessage
    ) {
        this.getOrCreateConversation(meetingId).removeMessage(message);
    }
}

export const chatStore = new ChatStore();
